import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { SimulateEntry } from '@cocoonspace/sdk-js/types/simulateEntry'
import { useMemo } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Trans, useLingui } from '@lingui/react/macro'

import { InfoPopover } from '~/components/atoms/info-popover'
import { Price } from '~/components/price'

const { getTimeAsMinutes } = dateTimeUtils.utils

export const FooterPriceUpdate = ({
	bookingSimulation,
	event,
}: {
	bookingSimulation: NonNullable<SimulateEntry>
	event: Event
}) => {
	const { t } = useLingui()

	const durationDelta = useMemo(() => {
		let sign = ''
		let title: React.ReactNode

		const eventDuration =
			event.start && event.end
				? getTimeAsMinutes(event.end) - getTimeAsMinutes(event.start)
				: 0

		const simulationDuration =
			bookingSimulation?.start && bookingSimulation?.end
				? getTimeAsMinutes(bookingSimulation?.end) -
					getTimeAsMinutes(bookingSimulation?.start)
				: 0

		const diff = simulationDuration - eventDuration
		const timeDiff = Math.abs(diff)

		const duration =
			timeDiff === 30
				? t`1/2h`
				: dateTimeUtils
						.duration(timeDiff, 'minutes')
						.format('H[h]mm')
						.replace('h00', 'h')

		if (diff === 0) {
			sign = 'equal'
			title = (
				<Trans>
					La modification du créneau n’implique{' '}
					<strong>aucun frais supplémentaire</strong>
				</Trans>
			)
		} else {
			sign = diff > 0 ? 'pos' : 'neg'

			title =
				diff > 0 ? (
					<Trans>
						Vous ajoutez <strong>{duration}</strong> à votre réservation
						initiale
					</Trans>
				) : (
					<Trans>
						Vous retirez <strong>{duration}</strong> à votre réservation
						initiale, nous vous remboursons instantanément la différence.
					</Trans>
				)
		}

		return {
			duration,
			sign,
			title,
		}
	}, [event, bookingSimulation, t])

	return (
		<div className='min-h-[45px]'>
			<div className='flex flex-row gap-2'>
				<div className='text-sm'>
					{durationDelta.sign === 'neg'
						? t`soit ${durationDelta.duration} en moins`
						: t`soit ${durationDelta.duration} en supplément`}
				</div>

				<InfoPopover>{durationDelta.title}</InfoPopover>
			</div>

			{bookingSimulation?.price_delta === 0 ? (
				<div className='font-bold'>{t`Gratuit`}</div>
			) : (
				<Price
					price={bookingSimulation?.price_delta}
					taxClassName='mb-px text-xxs font-normal'
					taxType='ht'
				/>
			)}
		</div>
	)
}
